import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "./Panel";
import { RateInput } from "./RateInput";
import { getIn } from "./ratePaths";
import { useBandIds } from "./useBandIds";

/** The band number, every figure in the band, and a way to drop it. */
const BandRow = ({ service, table, row, rowIndex, columns, onCell, onRemove }) => (
  <tr className="hover:bg-[color:var(--tt-surface-2)]">
    <td className="px-3 py-2 font-amount text-xs text-muted-foreground">{rowIndex + 1}</td>
    {columns.map((column, colIndex) => (
      <td key={column.label} className="px-3 py-2">
        <RateInput
          testId={`rate-cell-${service}-${table.path}-${rowIndex}-${colIndex}`}
          ariaLabel={`${table.title} row ${rowIndex + 1} ${column.label}`}
          kind={column.type}
          value={row[colIndex]}
          onChange={(value) => onCell(rowIndex, colIndex, value)}
        />
      </td>
    ))}
    <td className="px-2 py-2 text-right">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Remove ${table.row_noun || "row"} ${rowIndex + 1}`}
        data-testid={`rate-remove-row-${service}-${table.path}-${rowIndex}`}
        onClick={() => onRemove(rowIndex)}
        className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
    </td>
  </tr>
);

/**
 * Work out the next band up from the pattern already in the table.
 *
 * If the admin has been stepping in even increments we carry that increment on;
 * otherwise we just move the ceiling up by something sensible so the new band
 * is never a duplicate of the one above it.
 */
function seedNextBand(rows, columns) {
  const last = rows[rows.length - 1] || columns.map(() => 0);
  const step = rows.length > 1 ? Number(last[0]) - Number(rows[rows.length - 2][0]) : 0;
  return last.map((cell, index) =>
    index === 0 ? Number(cell) + (step > 0 ? step : Math.max(1, Number(cell) || 1)) : cell,
  );
}

/** An ordered band table: turnover or volume ceilings and the fee for each. */
export const TableEditor = ({ service, table, draft, onChange }) => {
  const path = `${service}.${table.path}`;
  const rows = getIn(draft, path) || [];
  const columns = table.columns;
  const bandIds = useBandIds(rows.length);

  const setCell = (rowIndex, colIndex, value) => {
    const next = rows.map((row, i) =>
      i === rowIndex ? row.map((cell, j) => (j === colIndex ? value : cell)) : row,
    );
    onChange(path, next);
  };

  const addRow = () => {
    bandIds.added();
    onChange(path, [...rows, seedNextBand(rows, columns)]);
  };

  const removeRow = (rowIndex) => {
    if (rows.length <= 1) {
      toast.error("A band table needs at least one band.");
      return;
    }
    bandIds.removed(rowIndex);
    onChange(
      path,
      rows.filter((_row, i) => i !== rowIndex),
    );
  };

  return (
    <Panel title={table.title} help={table.help} testId={`rate-card-table-${service}-${table.path}`}>
      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-[color:var(--tt-teal-mist)]">
            <tr>
              <th className="w-12 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--tt-teal-deep)]">
                #
              </th>
              {columns.map((column) => (
                <th
                  key={column.label}
                  className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--tt-teal-deep)]"
                >
                  {column.label}
                </th>
              ))}
              <th className="w-12 px-3 py-2" aria-label="Remove band" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, rowIndex) => (
              <BandRow
                key={bandIds.ids[rowIndex] || `band-at-${rowIndex}`}
                service={service}
                table={table}
                row={row}
                rowIndex={rowIndex}
                columns={columns}
                onCell={setCell}
                onRemove={removeRow}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {rows.length} {table.row_noun || "row"}
          {rows.length === 1 ? "" : "s"} · the last one is the ceiling this calculator covers
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          data-testid={`rate-add-row-${service}-${table.path}`}
          className="bg-white"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Add {table.row_noun || "row"}
        </Button>
      </div>
    </Panel>
  );
};
